import { BACKEND_URL } from '@/lib/constants/api';

const STORAGE_KEY = 'retraced:context';

/**
 * Exchanges the stored refresh token for a fresh access token.
 *
 * The backend issues a 24h access token and a 30d refresh token, and `/refresh-token`
 * is the only way to turn the second into a new first. v2 never called it, so
 * every session died at the 24h mark and the user was bounced to login — daily.
 * The old dashboard has always renewed this way (`renewing-token.jsx`), which is
 * why it kept people signed in while v2 did not.
 *
 * Deliberately a bare `fetch` rather than `apiFetch`:
 *
 * - `apiFetch` attaches the *access* token, and this endpoint authenticates
 *   with the *refresh* token in the same header;
 * - `apiFetch` is what calls this on a 401, so going back through it would
 *   recurse;
 * - a failure here must not trigger the session-wipe-and-redirect that
 *   `apiFetch` performs — the caller decides what a failed refresh means.
 */

interface StoredSessionState {
  accessToken?: string | null;
  refreshToken?: string | null;
  [key: string]: unknown;
}

function readState(): StoredSessionState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: StoredSessionState };
    return parsed.state ?? null;
  } catch {
    return null;
  }
}

/**
 * Writes the new access token back, leaving every other field untouched — the
 * record is shared with the old dashboard's Zustand store, so replacing it
 * wholesale would drop impersonation, company scope and the refresh token.
 */
function writeAccessToken(accessToken: string): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as {
      state?: StoredSessionState;
      version?: number;
    };
    if (!parsed.state) return;
    parsed.state.accessToken = accessToken;
    parsed.state.isSessionExpired = false;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
  } catch {
    // A refresh we cannot persist is a refresh that did not happen; the next
    // 401 simply tries again.
  }
}

/**
 * In-flight refresh, shared by every caller.
 *
 * A single expired access token usually surfaces as several simultaneous 401s
 * (a page load fires many queries at once). Without this, each one would spend
 * the refresh token separately and race to write the result.
 */
let inFlight: Promise<boolean> | null = null;

async function performRefresh(): Promise<boolean> {
  const refreshToken = readState()?.refreshToken;
  if (!refreshToken) return false;

  try {
    const res = await fetch(`${BACKEND_URL}/authentication/refresh-token`, {
      method: 'POST',
      cache: 'no-store',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${refreshToken}`,
      },
      body: JSON.stringify({}),
    });

    // 419 is the backend's "refresh token invalid or blacklisted" — the session
    // is genuinely over and no retry will help.
    if (!res.ok) return false;

    const body = (await res.json()) as { accessToken?: string };
    if (!body.accessToken) return false;

    writeAccessToken(body.accessToken);
    return true;
  } catch {
    return false;
  }
}

/** True when a fresh access token was obtained and stored. */
export function refreshAccessToken(): Promise<boolean> {
  inFlight ??= performRefresh().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/** True when the session holds a refresh token worth spending on a retry. */
export function hasRefreshToken(): boolean {
  return Boolean(readState()?.refreshToken);
}
